import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  Type,
  TemplateRef,
} from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';

import {
  BusinessDocument as BusinessDocumentService,
  BusinessDocumentPipelineBundle,
} from '../../../core/services/business-document.service';
import { ReportQuery, ReportQueryService } from '../../../core/services/report-query';
import { AuthService } from '../../../core/services/auth.service';
import { S3FileService } from '../../../core/services/s3-file.service';
import {
  RecordListPreferences,
  RecordListPreferencesService,
} from '../../../core/services/record-list-preferences.service';
import {
  BusinessDocumentKanbanColumn,
  BusinessDocumentKanbanMove,
} from '../../business-document/business-document-kanban/business-document-kanban';
import { BusinessDocumentPipelineOption } from '../../business-document/business-document-pipeline-picker/business-document-pipeline-picker';
import { BusinessDocumentListView, BusinessDocumentViewMode } from '../../services/business-document-list-view';
import {
  RecordListAction,
  RecordListActionCondition,
  RecordListActionEvent,
  RecordListBusinessDocument,
  RecordListBusinessDocumentMoveEvent,
  RecordListBusinessDocumentOpenEvent,
  RecordListCardComponent,
  RecordListCardConfiguration,
  RecordListColumn,
  RecordListFilterOperator,
  RecordListLoadedEvent,
  RecordListResolvedOption,
  RecordListState,
  RecordListViewMode,
  RecordListTree,
  RecordListTreeRow,
} from './record-list.types';
import { projectRecordTree } from './record-list-tree';
import {
  DEFAULT_RECORD_LIST_STATE,
  buildRecordListQuery,
  filterStateSignature,
  interpolateRecordTemplate,
  normalizeRecordListState,
  resolveRecordPath,
  validateRecordListColumns,
} from './record-list.utils';

@Component({
  selector: 'app-record-list',
  standalone: false,
  templateUrl: './record-list.html',
  styleUrl: './record-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordList implements OnChanges, OnDestroy {
  /** Stable identifier for this list's user-and-organisation UI preferences. */
  @Input() configurationId = '';
  @Input() query: ReportQuery | null = null;
  /** Canonical owner-loaded records for lists whose permission scope cannot be represented by RQL. */
  @Input() records: Array<Record<string, unknown>> | null = null;
  /**
   * Total supplied by a server-paginated owner endpoint. When set, records are
   * treated as the current page and search/filter/sort state is emitted back to
   * the owner instead of being applied to that partial page in the browser.
   */
  @Input() recordsTotal: number | null = null;
  @Input() recordsLoading = false;
  @Input() recordsError = '';
  /** Decimal strings keyed by output column; the owner uses the same filters/scope as its page. */
  @Input() recordsTotals: Readonly<Record<string, string | number>> | null = null;
  @Input() columns: RecordListColumn[] = [];
  /** Owner-authored controls for a cell, with row and column as template context. */
  @Input() cellTemplates: Readonly<Record<string, TemplateRef<any>>> = {};
  @Input() actions: RecordListAction[] = [];
  @Input() state: Partial<RecordListState> | null = null;
  @Input() selectionKey = 'id';
  @Input() emptyLabel = 'No records match this view.';
  @Input() refreshToken = 0;
  @Input() rowEnricher: ((row: Record<string, unknown>) => Record<string, unknown>) | null = null;
  @Input() businessDocument: RecordListBusinessDocument | null = null;
  @Input() viewTypes: RecordListViewMode[] = ['list'];
  @Input() initialView: RecordListViewMode = 'list';
  @Input() card: RecordListCardConfiguration | null = null;
  @Input() showToolbar = true;
  @Input() showPagination = true;
  @Input() tree: RecordListTree | null = null;
  @Input() treeCollapsedIds: string[] = [];
  @Output() treeCollapsedIdsChange = new EventEmitter<string[]>();
  /** Optional owner-provided link. Hidden unless a page explicitly enables it. */
  @Input() editPipelineRoute: string | any[] | null = null;
  @Input() editPipelineLabel = 'Edit the pipeline';

  @Output() stateChange = new EventEmitter<RecordListState>();
  @Output() action = new EventEmitter<RecordListActionEvent>();
  @Output() loaded = new EventEmitter<RecordListLoadedEvent>();
  @Output() recordOpen = new EventEmitter<Record<string, unknown>>();
  @Output() businessDocumentOpen = new EventEmitter<RecordListBusinessDocumentOpenEvent>();
  @Output() businessDocumentMove = new EventEmitter<RecordListBusinessDocumentMoveEvent>();

  rows: Array<Record<string, unknown>> = [];
  total = 0;
  loading = false;
  errorMessage = '';
  configError = '';
  searchText = '';
  internalState: RecordListState = normalizeRecordListState(DEFAULT_RECORD_LIST_STATE);
  showAllFilters = false;
  openFilterKey: string | null = null;
  actionsOpen = false;
  viewMode: BusinessDocumentViewMode = 'list';
  pipelines: BusinessDocumentPipelineBundle[] = [];
  selectedPipelineId = '';
  pipelineLoading = false;
  pipelineError = '';
  preferenceError = '';
  treeMatchedCount = 0;
  private treeRows = new Map<string, RecordListTreeRow>();
  private treeCache: { signature: string; rows: Array<Record<string, unknown>> } | null = null;

  readonly optionsByColumn: Record<string, RecordListResolvedOption[]> = {};
  readonly optionTotals: Record<string, number> = {};
  readonly optionLoading: Record<string, boolean> = {};
  readonly optionSearch: Record<string, string> = {};
  readonly selectedIds = new Set<string>();
  readonly numberOperators: Array<{ value: RecordListFilterOperator; label: string }> = [
    { value: 'eq', label: 'Equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'lt', label: 'Less than' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'between', label: 'Between' },
  ];
  readonly dateOperators: Array<{ value: RecordListFilterOperator; label: string }> = [
    { value: 'eq', label: 'On' },
    { value: 'gt', label: 'After' },
    { value: 'gte', label: 'On or after' },
    { value: 'lt', label: 'Before' },
    { value: 'lte', label: 'On or before' },
    { value: 'between', label: 'Between' },
  ];

  private requestSequence = 0;
  private optionSequence: Record<string, number> = {};
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private optionTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  private loadQueued = false;
  private lastStateSignature = '';
  private readonly loggedErrors = new Set<string>();
  private viewRegistration?: symbol;
  private preferenceSequence = 0;
  private preferenceWriteChain: Promise<void> = Promise.resolve();
  private savedPageSize: number | null = null;
  private initialPageSize: number | null = null;
  private preferencesLoaded = false;
  private readonly subscriptions = new Subscription();
  private readonly avatarUrls = new Map<string, string>();
  private readonly pendingAvatarKeys = new Set<string>();

  constructor(
    private readonly reportQuery: ReportQueryService,
    private readonly cdr: ChangeDetectorRef,
    private readonly businessDocuments: BusinessDocumentService,
    private readonly listView: BusinessDocumentListView,
    private readonly preferences: RecordListPreferencesService,
    private readonly auth: AuthService,
    private readonly files: S3FileService,
  ) {
    this.subscriptions.add(
      this.listView.state$.subscribe((state) => {
        if (!this.viewRegistration || !state.visible || this.viewMode === state.mode) {
          return;
        }
        this.viewMode = state.mode;
        this.clearSelection();
        void this.loadRecords();
        if (this.preferencesLoaded) {
          void this.savePreferences();
        }
        this.cdr.markForCheck();
      }),
    );
    this.subscriptions.add(this.auth.organisationSwitched$.subscribe(() => {
      this.preferenceSequence++;
      this.treeCache = null;
      this.savedPageSize = null;
      this.preferencesLoaded = false;
      this.internalState = normalizeRecordListState({
        ...this.state,
        page: 1,
        perPage: this.initialPageSize ?? DEFAULT_RECORD_LIST_STATE.perPage,
      });
      this.lastStateSignature = filterStateSignature(this.internalState);
      this.stateChange.emit(this.internalState);
      this.configureViewMode();
      void this.loadPreferences();
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    let stateChanged = false;

    if (
      changes['columns']
      || changes['query']
      || changes['businessDocument']
      || changes['viewTypes']
      || changes['card']
      || changes['tree']
      || changes['recordsTotal']
    ) {
      this.configError = this.configurationError();
      if (this.configError) {
        console.error('[RecordList] ' + this.configError, {
          columns: this.columns,
          query: this.query,
          businessDocument: this.businessDocument,
        });
      }
    }

    if (changes['columns']) {
      this.loadStaticOptions();
    }

    if (changes['actions'] && !this.hasActions) {
      this.clearSelection();
    }

    if (
      changes['businessDocument']
      || changes['viewTypes']
      || changes['initialView']
      || changes['card']
    ) {
      this.configureViewMode();
    }

    if (changes['state']) {
      if (this.initialPageSize === null) {
        this.initialPageSize = normalizeRecordListState(this.state).perPage;
      }
      const next = normalizeRecordListState({
        ...this.state,
        ...(this.savedPageSize !== null ? { perPage: this.savedPageSize } : {}),
      });
      const signature = filterStateSignature(next);
      if (signature !== this.lastStateSignature) {
        this.internalState = next;
        this.searchText = next.search;
        this.lastStateSignature = signature;
        stateChanged = true;
      }
    }

    if (changes['configurationId'] && this.configurationId) {
      this.preferenceSequence++;
      this.savedPageSize = null;
      this.preferencesLoaded = false;
      void this.loadPreferences();
    }

    if (
      changes['query']
      || changes['records']
      || changes['recordsTotal']
      || changes['recordsLoading']
      || changes['recordsError']
      || changes['columns']
      || stateChanged
      || changes['refreshToken']
      || changes['tree']
      || changes['treeCollapsedIds']
    ) {
      this.queueLoad();
    }
  }

  ngOnDestroy(): void {
    this.requestSequence++;
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    Object.values(this.optionTimers).forEach((timer) => clearTimeout(timer));
    this.subscriptions.unsubscribe();
    if (this.viewRegistration) {
      this.listView.release(this.viewRegistration);
    }
  }

  get displayColumns(): RecordListColumn[] {
    const primary = this.columns.find((column) => column.primary);
    return primary
      ? [primary, ...this.columns.filter((column) => column !== primary)]
      : this.columns;
  }

  get filterableColumns(): RecordListColumn[] {
    return this.displayColumns.filter((column) => column.filter?.filterable);
  }

  get initiallyVisibleFilters(): RecordListColumn[] {
    const prioritized = this.filterableColumns.filter((column) => column.filter?.prioritized);
    const visible = [...prioritized];
    for (const column of this.filterableColumns) {
      if (column.filter?.prioritized || visible.length >= 4) {
        continue;
      }
      visible.push(column);
    }
    return visible;
  }

  get hiddenFilters(): RecordListColumn[] {
    const visible = new Set(this.initiallyVisibleFilters.map((column) => column.key));
    return this.filterableColumns.filter((column) => !visible.has(column.key));
  }

  get visibleFilters(): RecordListColumn[] {
    return this.showAllFilters ? this.filterableColumns : this.initiallyVisibleFilters;
  }

  get selectedRecords(): Array<Record<string, unknown>> {
    return this.rows.filter((row, index) => this.selectedIds.has(this.rowId(row, index)));
  }

  get selectionCount(): number {
    return this.selectedIds.size;
  }

  get allSelected(): boolean {
    return (
      this.rows.length > 0 &&
      this.rows.every((row, index) => this.selectedIds.has(this.rowId(row, index)))
    );
  }

  get totalPages(): number {
    return this.total > 0 ? Math.ceil(this.total / this.internalState.perPage) : 0;
  }

  get activeFilterCount(): number {
    return Object.keys(this.internalState.filters).length + (this.internalState.search ? 1 : 0);
  }

  get hasColumnTotals(): boolean { return this.displayColumns.some(column => column.total); }

  columnTotal(column: RecordListColumn): string {
    if (!column.total || this.loading || this.errorMessage || this.configError || this.recordsTotals?.[column.key] === undefined) return '—';
    // Keep the server's decimal precision; converting to Number can lose cents on large totals.
    return String(this.recordsTotals[column.key]);
  }

  get visibleActions(): RecordListAction[] {
    return this.actions.filter((item) => this.isActionVisible(item));
  }

  get hasActions(): boolean {
    return this.actions.length > 0;
  }

  get businessDocumentEnabled(): boolean {
    return Boolean(this.businessDocument);
  }

  get cardConfiguration(): RecordListCardConfiguration | null {
    return this.businessDocument?.card ?? this.card;
  }

  get cardComponent(): Type<RecordListCardComponent> | null {
    return this.cardConfiguration?.component ?? null;
  }

  get cardColumns(): number {
    const requested = this.cardConfiguration?.columns ?? 4;
    return Math.min(6, Math.max(1, Math.trunc(requested)));
  }

  get availableViewTypes(): RecordListViewMode[] {
    const requested = this.businessDocument && this.viewTypes.length === 1 && this.viewTypes[0] === 'list'
      ? ['list', 'card', 'kanban'] as RecordListViewMode[]
      : this.viewTypes;
    return [...new Set(requested)];
  }

  cardInputs(row: Record<string, unknown>): Record<string, unknown> {
    return { item: row, context: this.cardConfiguration?.context ?? {} };
  }

  cardRouterLink(row: Record<string, unknown>): string | null {
    const template = this.cardConfiguration?.link?.template;
    return template ? interpolateRecordTemplate(template, row, 'record card link') : null;
  }

  openCard(row: Record<string, unknown>): void {
    if (this.businessDocument) {
      const businessDocumentId = String(this.businessDocumentValue(row, 'documentIdField') ?? '');
      if (businessDocumentId) {
        this.businessDocumentOpen.emit({ businessDocumentId, record: row });
      }
      return;
    }
    this.recordOpen.emit(row);
  }

  get pipelineOptions(): BusinessDocumentPipelineOption[] {
    return this.pipelines.map((bundle) => ({
      value: bundle.pipeline.id,
      label: bundle.pipeline.name,
    }));
  }

  get kanbanColumns(): BusinessDocumentKanbanColumn[] {
    const pipeline = this.selectedPipeline;
    if (!pipeline) {
      return [];
    }
    const activeSteps = pipeline.steps
      .filter((entry) => entry.step.active)
      .sort((left, right) => left.step.sequence - right.step.sequence);
    return activeSteps.map((entry) => {
      const step = entry.step;
      const cards = this.rows
        .filter((row) => {
          const pipelineMatches = !this.selectedPipelineId
            || String(this.businessDocumentValue(row, 'pipelineIdField') ?? '') === this.selectedPipelineId;
          return pipelineMatches
            && String(this.businessDocumentValue(row, 'currentStepIdField') ?? '') === step.id;
        })
        .map((row) => this.kanbanCard(row));
      const transitions = pipeline.transitions
        .filter((transition) => transition.active && transition.from_step_id === step.id)
        .map((transition) => ({
          toColumnId: transition.to_step_id,
          isDefault: transition.is_default,
        }));
      return {
        id: step.id,
        title: step.name,
        accent: step.color ?? '',
        summary: `${cards.length} record${cards.length === 1 ? '' : 's'}`,
        isInitial: step.is_initial,
        isTerminal: step.is_terminal,
        transitions,
        defaultNextColumnId:
          transitions.find((transition) => transition.isDefault)?.toColumnId ?? null,
        cards,
      };
    });
  }

  get kanbanDraggable(): boolean {
    return this.businessDocument?.draggable !== false && !this.loading;
  }

  headerLabel(column: RecordListColumn): string {
    return typeof column.label === 'string' ? column.label : column.label.name;
  }

  headerClass(column: RecordListColumn): string {
    return typeof column.label === 'string' ? '' : (column.label.className ?? '');
  }

  sortIcon(column: RecordListColumn): string {
    if (this.internalState.sort?.key !== column.key) {
      return '↕';
    }
    return this.internalState.sort.direction === 'asc' ? '↑' : '↓';
  }

  sortBy(column: RecordListColumn): void {
    if (!column.sortable || this.loading) {
      return;
    }
    const direction =
      this.internalState.sort?.key === column.key && this.internalState.sort.direction === 'asc'
        ? 'desc'
        : 'asc';
    this.updateState({
      ...this.internalState,
      page: 1,
      sort: { key: column.key, direction },
    });
  }

  onSortKeydown(event: KeyboardEvent, column: RecordListColumn): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.sortBy(column);
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      this.updateState({ ...this.internalState, search: value.trim(), page: 1 });
    }, 400);
  }

  clearAllFilters(): void {
    this.searchText = '';
    this.updateState({ ...this.internalState, search: '', filters: {}, page: 1 });
  }

  toggleFilter(column: RecordListColumn): void {
    if (this.openFilterKey === column.key) {
      this.openFilterKey = null;
      return;
    }
    this.openFilterKey = column.key;
    void this.ensureOptions(column);
  }

  setFilterOpen(column: RecordListColumn, open: boolean): void {
    this.openFilterKey = open
      ? column.key
      : this.openFilterKey === column.key
        ? null
        : this.openFilterKey;
    if (open) {
      void this.ensureOptions(column);
    }
  }

  isOptionFilter(column: RecordListColumn): boolean {
    return (column.filter?.type ?? 'equal') === 'equal';
  }

  optionsFor(column: RecordListColumn): RecordListResolvedOption[] {
    const search = (this.optionSearch[column.key] ?? '').trim().toLowerCase();
    const options = this.optionsByColumn[column.key] ?? [];
    if (!search || this.isDynamicOptionFilter(column)) {
      return options;
    }
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(search) ||
        String(option.value).toLowerCase().includes(search),
    );
  }

  hiddenOptionCount(column: RecordListColumn): number {
    return Math.max(0, (this.optionTotals[column.key] ?? 0) - this.optionsFor(column).length);
  }

  onOptionSearchChange(column: RecordListColumn, value: string): void {
    this.optionSearch[column.key] = value;
    if (!this.isDynamicOptionFilter(column)) {
      return;
    }
    if (this.optionTimers[column.key]) {
      clearTimeout(this.optionTimers[column.key]);
    }
    this.optionTimers[column.key] = setTimeout(() => {
      delete this.optionTimers[column.key];
      void this.loadDynamicOptions(column, value.trim());
    }, 300);
  }

  isOptionSelected(column: RecordListColumn, option: RecordListResolvedOption): boolean {
    const values = this.filterValues(column);
    return values.some((value) => String(value) === String(option.value));
  }

  toggleOption(
    column: RecordListColumn,
    option: RecordListResolvedOption,
    selected: boolean,
  ): void {
    const values = this.filterValues(column);
    const next = selected
      ? column.filter?.multiple === false ? [option.value]
        : [...values.filter((value) => String(value) !== String(option.value)), option.value]
      : values.filter((value) => String(value) !== String(option.value));
    this.setFilter(column, 'eq', next);
  }

  clearFilter(column: RecordListColumn): void {
    const filters = { ...this.internalState.filters };
    delete filters[column.key];
    this.updateState({ ...this.internalState, filters, page: 1 });
  }

  filterButtonLabel(column: RecordListColumn): string {
    const state = this.internalState.filters[column.key];
    if (!state) {
      return this.headerLabel(column);
    }
    const values = Array.isArray(state.value) ? state.value : [state.value];
    if (this.isOptionFilter(column)) {
      if (values.length === 1) {
        const option = (this.optionsByColumn[column.key] ?? []).find(
          (item) => String(item.value) === String(values[0]),
        );
        return option?.label ?? this.headerLabel(column) + ': 1';
      }
      return this.headerLabel(column) + ': ' + values.length;
    }
    const label = this.operatorOptions(column).find((item) => item.value === state.operator)?.label;
    if (state.operator === 'between') {
      return (
        this.headerLabel(column) + ': ' + String(values[0] ?? '') + ' → ' + String(values[1] ?? '')
      );
    }
    return (
      this.headerLabel(column) + ': ' + (label ?? state.operator) + ' ' + String(values[0] ?? '')
    );
  }

  operatorOptions(column: RecordListColumn): Array<{
    value: RecordListFilterOperator;
    label: string;
  }> {
    const options = column.filter?.type === 'date' ? this.dateOperators : this.numberOperators;
    const allowed = column.filter?.operators;
    return allowed?.length ? options.filter((item) => allowed.includes(item.value)) : options;
  }

  filterOperator(column: RecordListColumn): RecordListFilterOperator {
    return (
      this.internalState.filters[column.key]?.operator ??
      (column.filter?.type === 'date' ? 'gte' : 'eq')
    );
  }

  filterValues(column: RecordListColumn): Array<string | number | boolean> {
    const value = this.internalState.filters[column.key]?.value;
    if (value == null || value === '') {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  }

  setOperator(column: RecordListColumn, operator: RecordListFilterOperator): void {
    const values = this.filterValues(column);
    this.setFilter(
      column,
      operator,
      operator === 'between' ? [values[0] ?? '', values[1] ?? ''] : (values[0] ?? ''),
    );
  }

  setComparisonValue(column: RecordListColumn, value: string, index = 0): void {
    const operator = this.filterOperator(column);
    if (operator === 'between') {
      const values = this.filterValues(column);
      values[index] = value;
      this.setFilter(column, operator, values);
      return;
    }
    this.setFilter(column, operator, value);
  }

  comparisonInputType(column: RecordListColumn): 'number' | 'date' | 'datetime-local' {
    if (column.filter?.type === 'number') {
      return 'number';
    }
    return column.filter?.valueType === 'datetime' ? 'datetime-local' : 'date';
  }

  rawValue(row: Record<string, unknown>, column: RecordListColumn): unknown {
    const resolved = resolveRecordPath(row, column.key);
    if (!resolved.found) {
      this.logOnce(
        'value:' + column.key,
        "[RecordList] Query result does not contain configured column '" + column.key + "'.",
        row,
      );
      return null;
    }
    return resolved.value;
  }

  displayValue(row: Record<string, unknown>, column: RecordListColumn): string {
    const template = column.value ?? '{{query_result.' + column.key + '}}';
    const rendered = interpolateRecordTemplate(template, row, "column '" + column.key + "'");
    if (!rendered) {
      return column.fallback ?? '—';
    }
    if (column.badge?.labels?.[rendered] != null) {
      return column.badge.labels[rendered];
    }
    if (column.format === 'boolean') {
      return rendered === 'true' ? 'Yes' : rendered === 'false' ? 'No' : rendered;
    }
    if (column.format === 'number') {
      const value = Number(rendered);
      return Number.isFinite(value) ? new Intl.NumberFormat().format(value) : rendered;
    }
    if (column.format === 'currency') {
      const value = Number(rendered);
      const rowCurrency = column.currencyField
        ? resolveRecordPath(row, column.currencyField).value
        : null;
      const currency = typeof rowCurrency === 'string' && rowCurrency.trim()
        ? rowCurrency.trim().toUpperCase()
        : column.currency ?? 'TZS';
      return Number.isFinite(value)
        ? new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 2,
          }).format(value)
        : rendered;
    }
    if (column.format === 'percentage') {
      const value = Number(rendered);
      return Number.isFinite(value)
        ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value) + '%'
        : rendered;
    }
    if (column.format === 'date' || column.format === 'datetime') {
      const value = new Date(rendered);
      if (!Number.isNaN(value.getTime())) {
        return new Intl.DateTimeFormat(undefined, {
          dateStyle: 'medium',
          ...(column.format === 'datetime' ? { timeStyle: 'short' as const } : {}),
        }).format(value);
      }
    }
    return rendered;
  }

  cellTitle(row: Record<string, unknown>, column: RecordListColumn): string {
    if (column.titleField) {
      const detail = row[column.titleField];
      if (Array.isArray(detail) && detail.length) return detail.map(String).join('\n');
      if (typeof detail === 'string' && detail.trim()) return detail;
    }
    return this.displayValue(row, column);
  }

  linkFor(row: Record<string, unknown>, column: RecordListColumn): string {
    return column.link
      ? interpolateRecordTemplate(column.link.template, row, "link for '" + column.key + "'")
      : '';
  }

  hasCellLink(row: Record<string, unknown>, column: RecordListColumn): boolean {
    const link = column.link;
    return Boolean(link && (!link.whenField || row[link.whenField]));
  }

  avatarImage(row: Record<string, unknown>, column: RecordListColumn): string {
    if (column.avatar?.image) {
      return interpolateRecordTemplate(column.avatar.image, row, "avatar image for '" + column.key + "'");
    }
    if (!column.avatar?.imageKey) return '';
    const key = interpolateRecordTemplate(column.avatar.imageKey, row, "avatar key for '" + column.key + "'");
    if (!key) return '';
    if (this.avatarUrls.has(key)) return this.avatarUrls.get(key) ?? '';
    if (!this.pendingAvatarKeys.has(key)) {
      this.pendingAvatarKeys.add(key);
      void firstValueFrom(this.files.getFileUrlValue(key))
        .then((url) => this.avatarUrls.set(key, url ?? ''))
        .catch(() => this.avatarUrls.set(key, ''))
        .finally(() => {
          this.pendingAvatarKeys.delete(key);
          this.cdr.markForCheck();
        });
    }
    return '';
  }

  avatarInitials(row: Record<string, unknown>, column: RecordListColumn): string {
    const source = column.avatar?.initial
      ? interpolateRecordTemplate(
          column.avatar.initial,
          row,
          "avatar initial for '" + column.key + "'",
        )
      : this.displayValue(row, column);
    const initials = source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
    return initials || '?';
  }

  avatarClass(column: RecordListColumn): string {
    const size = column.avatar?.size ?? 'sm';
    return 'avatar avatar-' + size + ' ' + (column.avatar?.className ?? 'bg-blue-lt');
  }

  badgeClass(row: Record<string, unknown>, column: RecordListColumn): string {
    const value = String(this.rawValue(row, column) ?? '');
    return (
      'badge ' + (column.badge?.classes?.[value] ?? column.badge?.className ?? 'bg-secondary-lt')
    );
  }

  actionClass(item: RecordListAction): string {
    return item.style && item.style !== 'default' ? 'text-' + item.style : '';
  }

  isActionVisible(item: RecordListAction): boolean {
    return this.actions.includes(item);
  }

  isActionEnabled(item: RecordListAction): boolean {
    const minimum = item.minSelection ?? 1;
    const maximum = item.maxSelection ?? Number.POSITIVE_INFINITY;
    return !this.loading && this.selectionCount >= minimum && this.selectionCount <= maximum
      && (!item.condition || this.matchesCondition(item.condition));
  }

  runAction(item: RecordListAction): void {
    if (!this.isActionEnabled(item) || !this.isActionVisible(item)) {
      return;
    }
    this.actionsOpen = false;
    this.action.emit({ actionId: item.id, records: this.selectedRecords });
  }

  selectPipeline(pipelineId: string): void {
    if (this.selectedPipelineId === pipelineId) {
      return;
    }
    this.selectedPipelineId = pipelineId;
    this.clearSelection();
    this.updateState({ ...this.internalState, page: 1 });
  }

  openKanbanCard(businessDocumentId: string): void {
    const record = this.rows.find(
      (row) => String(this.businessDocumentValue(row, 'documentIdField') ?? '') === businessDocumentId,
    );
    if (!record) {
      this.logOnce(
        `kanban-open:${businessDocumentId}`,
        `[RecordList] Could not resolve Kanban card '${businessDocumentId}' to an RQL row.`,
        this.rows,
      );
      return;
    }
    this.businessDocumentOpen.emit({ businessDocumentId, record });
  }

  moveKanbanCard(move: BusinessDocumentKanbanMove): void {
    const pipeline = this.selectedPipeline;
    const transition = pipeline?.transitions.find(
      (candidate) =>
        candidate.active &&
        candidate.from_step_id === move.fromColumnId &&
        candidate.to_step_id === move.toColumnId,
    );
    const record = this.rows.find(
      (row) => String(this.businessDocumentValue(row, 'documentIdField') ?? '') === move.cardId,
    );
    if (!transition || !record) {
      this.pipelineError = 'That pipeline move is not available for this record.';
      this.cdr.markForCheck();
      return;
    }
    this.pipelineError = '';
    this.businessDocumentMove.emit({
      businessDocumentId: move.cardId,
      fromStepId: move.fromColumnId,
      toStepId: move.toColumnId,
      actionCode: transition.action_code,
      record,
    });
  }

  toggleRow(row: Record<string, unknown>, index: number, selected: boolean): void {
    const id = this.rowId(row, index);
    selected ? this.selectedIds.add(id) : this.selectedIds.delete(id);
  }

  toggleAll(selected: boolean): void {
    if (!selected) {
      this.selectedIds.clear();
      return;
    }
    this.rows.forEach((row, index) => this.selectedIds.add(this.rowId(row, index)));
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  isSelected(row: Record<string, unknown>, index: number): boolean {
    return this.selectedIds.has(this.rowId(row, index));
  }

  onPageChange(page: number): void {
    this.updateState({ ...this.internalState, page });
  }

  onPerPageChange(perPage: number): void {
    this.updateState({ ...this.internalState, page: 1, perPage });
    this.savedPageSize = this.internalState.perPage;
    if (this.preferencesLoaded) {
      void this.savePreferences();
    }
  }

  private async loadPreferences(): Promise<void> {
    if (!this.configurationId) return;
    const sequence = ++this.preferenceSequence;
    try {
      const response = await firstValueFrom(this.preferences.get(this.configurationId));
      if (sequence !== this.preferenceSequence) return;
      const preference = response.preferences;
      if (preference && this.availableViewTypes.includes(preference.viewMode)) {
        this.listView.setMode(preference.viewMode);
      }
      if (preference && Number.isInteger(preference.perPage)
        && preference.perPage >= 1 && preference.perPage <= 100) {
        this.savedPageSize = preference.perPage;
        if (this.internalState.perPage !== preference.perPage) {
          this.updateState({ ...this.internalState, page: 1, perPage: preference.perPage });
        }
      }
      this.preferenceError = '';
      this.preferencesLoaded = true;
      this.cdr.markForCheck();
    } catch (error) {
      if (sequence !== this.preferenceSequence) return;
      this.preferenceError = this.apiErrorMessage(error);
      this.preferencesLoaded = false;
      this.cdr.markForCheck();
    }
  }

  private async savePreferences(): Promise<void> {
    if (!this.configurationId || !this.preferencesLoaded) return;
    const configurationId = this.configurationId;
    const sequence = this.preferenceSequence;
    const preference: RecordListPreferences = {
      viewMode: this.viewMode,
      perPage: this.internalState.perPage,
    };
    this.preferenceWriteChain = this.preferenceWriteChain.then(async () => {
      if (sequence !== this.preferenceSequence) return;
      try {
        await firstValueFrom(this.preferences.save(configurationId, preference));
        if (sequence === this.preferenceSequence) this.preferenceError = '';
      } catch (error) {
        if (sequence === this.preferenceSequence) this.preferenceError = this.apiErrorMessage(error);
      }
      this.cdr.markForCheck();
    });
    await this.preferenceWriteChain;
  }

  trackColumn(_index: number, column: RecordListColumn): string {
    return column.key;
  }

  trackOption(_index: number, option: RecordListResolvedOption): string {
    return String(option.value);
  }

  readonly trackRow = (index: number, row: Record<string, unknown>): string =>
    this.rowId(row, index);

  treeRow(row: Record<string, unknown>): RecordListTreeRow | undefined {
    return this.tree ? this.treeRows.get(String(resolveRecordPath(row, this.tree.idKey).value)) : undefined;
  }

  retryTree(): void { this.treeCache = null; void this.loadRecords(); }

  toggleTreeRow(row: Record<string, unknown>): void {
    const node = this.treeRow(row);
    if (!node?.hasChildren || this.activeFilterCount) return;
    this.treeCollapsedIds = node.expanded ? [...this.treeCollapsedIds, node.id] : this.treeCollapsedIds.filter(id => id !== node.id);
    this.treeCollapsedIdsChange.emit(this.treeCollapsedIds);
    void this.loadRecords();
  }

  private async loadTreeRecords(): Promise<void> {
    const sequence = ++this.requestSequence;
    this.loading = true;
    this.errorMessage = '';
    this.rows = [];
    this.total = 0;
    this.treeRows.clear();
    this.cdr.markForCheck();
    try {
      let records = this.records;
      if (!records && this.query) {
        const signature = JSON.stringify([this.query, this.refreshToken]);
        if (this.treeCache?.signature === signature) records = this.treeCache.rows;
        else {
          records = [];
          let expected: number | undefined;
          // A complete bounded snapshot is mandatory. Never present a truncated tree as complete.
          while (true) {
            const result = await firstValueFrom(this.reportQuery.execute({ ...this.query,
              order_by: [{ref:this.tree!.idKey, direction:'asc'}], limit:500, offset:records.length, include_total:true }));
            if (sequence !== this.requestSequence) return;
            const total = result.meta.total_count;
            if (total === undefined || total > 10000 || (expected !== undefined && expected !== total)) throw new Error('The complete hierarchy could not be loaded safely. Refresh or narrow its owner scope and retry.');
            expected = total;
            records.push(...result.rows);
            if (records.length === total) break;
            if (!result.rows.length || records.length > total) throw new Error('The hierarchy changed while loading. Please retry.');
          }
          this.treeCache = {signature, rows:records};
        }
      }
      if (sequence !== this.requestSequence) return;
      const projection = projectRecordTree(records ?? [], this.tree!, this.columns, this.internalState, this.treeCollapsedIds);
      this.rows = projection.rows.map(node => node.row);
      this.treeRows = new Map(projection.rows.map(node => [node.id, node]));
      this.total = projection.total;
      this.treeMatchedCount = projection.matchedCount;
      this.selectedIds.clear();
      const pages = Math.max(1, Math.ceil(this.total / this.internalState.perPage));
      if (this.internalState.page > pages) { this.updateState({...this.internalState, page:pages}); return; }
      this.loaded.emit({rows:this.rows, total:this.total, result:{source:this.query?.source ?? 'owner', columns:[], rows:this.rows, meta:{row_count:this.rows.length, total_count:this.total}}});
    } catch (error) {
      if (sequence !== this.requestSequence) return;
      this.treeCache = null;
      this.rows = [];
      this.total = 0;
      this.treeMatchedCount = 0;
      this.errorMessage = this.apiErrorMessage(error);
    } finally {
      if (sequence === this.requestSequence) { this.loading = false; this.cdr.detectChanges(); }
    }
  }

  private queueLoad(): void {
    if (this.loadQueued) {
      return;
    }
    this.loadQueued = true;
    queueMicrotask(() => {
      this.loadQueued = false;
      void this.loadRecords();
    });
  }

  private async loadRecords(): Promise<void> {
    if (this.configError) {
      return;
    }
    if (this.tree) {
      if (this.recordsLoading || this.recordsError) {
        this.requestSequence++;
        this.rows = [];
        this.total = 0;
        this.loading = this.recordsLoading;
        this.errorMessage = this.recordsError;
        this.selectedIds.clear();
        this.cdr.markForCheck();
        return;
      }
      await this.loadTreeRecords();
      return;
    }
    if (this.records) {
      this.loadOwnerRecords();
      return;
    }
    if (!this.query) return;
    const sequence = ++this.requestSequence;
    this.loading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    try {
      const result = await firstValueFrom(
        this.reportQuery.execute(
          buildRecordListQuery(
            this.businessDocumentQuery(this.query),
            this.columns,
            this.internalState,
          ),
        ),
      );
      if (sequence !== this.requestSequence) {
        return;
      }
      this.rows = result.rows.map((row) => this.rowEnricher ? this.rowEnricher(row) : row);
      this.total = result.meta.total_count ?? result.meta.row_count;
      this.selectedIds.clear();
      const totalPages = this.total > 0 ? Math.ceil(this.total / this.internalState.perPage) : 0;
      if (totalPages > 0 && this.internalState.page > totalPages) {
        this.updateState({ ...this.internalState, page: totalPages });
        return;
      }
      this.loaded.emit({ rows: result.rows, total: this.total, result });
    } catch (error) {
      if (sequence !== this.requestSequence) {
        return;
      }
      this.rows = [];
      this.total = 0;
      this.errorMessage = this.apiErrorMessage(error);
      console.error('[RecordList] RQL query failed.', error);
    } finally {
      if (sequence === this.requestSequence) {
        this.loading = false;
        this.cdr.detectChanges();
      }
    }
  }

  private loadOwnerRecords(): void {
    this.loading = this.recordsLoading;
    this.errorMessage = this.recordsError;
    if (this.loading) {
      this.rows = [];
      this.total = this.recordsTotal ?? 0;
      this.selectedIds.clear();
      this.cdr.markForCheck();
      return;
    }
    if (this.recordsTotal !== null) {
      this.rows = [...(this.records ?? [])];
      this.total = Math.max(0, this.recordsTotal);
      this.selectedIds.clear();
      if (!this.loading) {
        const result = {
          source: 'owner',
          columns: [],
          rows: this.rows,
          meta: { row_count: this.rows.length, total_count: this.total },
        };
        this.loaded.emit({ rows: this.rows, total: this.total, result });
      }
      this.cdr.markForCheck();
      return;
    }
    const search = this.internalState.search.toLocaleLowerCase();
    let records = [...(this.records ?? [])];
    if (search) {
      const searchable = this.columns.filter((column) => column.filter?.searchable);
      records = records.filter((record) => searchable.some((column) =>
        String(resolveRecordPath(record, column.key).value ?? '').toLocaleLowerCase().includes(search),
      ));
    }
    records = records.filter((record) => Object.entries(this.internalState.filters).every(([key, state]) => {
      const column = this.columns.find((candidate) => candidate.key === key);
      if (!column) return true;
      const actual = resolveRecordPath(record, column.key).value;
      const expected = Array.isArray(state.value) ? state.value : [state.value];
      if (state.operator === 'eq') return expected.some((value) => String(value) === String(actual));
      const left = typeof actual === 'number' ? actual : Date.parse(String(actual));
      const values = expected.map((value) => typeof value === 'number' ? value : Date.parse(String(value)));
      if (!Number.isFinite(left) || values.some((value) => !Number.isFinite(value))) return false;
      if (state.operator === 'between') return left >= values[0] && left <= values[1];
      if (state.operator === 'gt') return left > values[0];
      if (state.operator === 'gte') return left >= values[0];
      if (state.operator === 'lt') return left < values[0];
      return left <= values[0];
    }));
    const sort = this.internalState.sort;
    if (sort) {
      records.sort((left, right) => {
        const a = resolveRecordPath(left, sort.key).value;
        const b = resolveRecordPath(right, sort.key).value;
        const order = String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });
        return sort.direction === 'desc' ? -order : order;
      });
    }
    this.total = records.length;
    const start = (this.internalState.page - 1) * this.internalState.perPage;
    this.rows = records.slice(start, start + this.internalState.perPage);
    this.loading = false;
    this.selectedIds.clear();
    const result = { source: 'owner', columns: [], rows: this.rows, meta: { row_count: this.rows.length, total_count: this.total } };
    this.loaded.emit({ rows: this.rows, total: this.total, result });
    this.cdr.markForCheck();
  }

  private updateState(state: RecordListState): void {
    this.internalState = normalizeRecordListState(state);
    this.lastStateSignature = filterStateSignature(this.internalState);
    this.stateChange.emit(this.internalState);
    if (this.records && this.recordsTotal !== null) {
      return;
    }
    void this.loadRecords();
  }

  private setFilter(
    column: RecordListColumn,
    operator: RecordListFilterOperator,
    value: string | number | boolean | Array<string | number | boolean>,
  ): void {
    const filters = { ...this.internalState.filters };
    const values = Array.isArray(value) ? value : [value];
    if (values.length === 0 || values.every((item) => String(item).trim() === '')) {
      delete filters[column.key];
    } else {
      filters[column.key] = { operator, value };
    }
    this.updateState({ ...this.internalState, filters, page: 1 });
  }

  private loadStaticOptions(): void {
    for (const column of this.columns) {
      const filter = column.filter;
      if (!filter || !this.isOptionFilter(column)) {
        continue;
      }
      if (filter.options?.length) {
        this.optionsByColumn[column.key] = filter.options.map((item) => ({ ...item }));
      } else if (filter.labels) {
        this.optionsByColumn[column.key] = Object.entries(filter.labels).map(([value, label]) => ({
          value,
          label,
        }));
      } else if (filter.valueType === 'boolean') {
        this.optionsByColumn[column.key] = [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ];
      }
      if (this.optionsByColumn[column.key]) {
        this.optionTotals[column.key] = this.optionsByColumn[column.key].length;
      }
    }
  }

  private isDynamicOptionFilter(column: RecordListColumn): boolean {
    const filter = column.filter;
    return (
      Boolean(
        filter &&
        this.isOptionFilter(column) &&
        !filter.options?.length &&
        !filter.labels &&
        filter.valueType !== 'boolean',
      ) || Boolean(filter?.optionQuery)
    );
  }

  private async ensureOptions(column: RecordListColumn): Promise<void> {
    if (!this.isOptionFilter(column) || !this.isDynamicOptionFilter(column)) {
      return;
    }
    if (this.optionsByColumn[column.key] && !this.optionSearch[column.key]) {
      return;
    }
    await this.loadDynamicOptions(column, this.optionSearch[column.key] ?? '');
  }

  private async loadDynamicOptions(column: RecordListColumn, search: string): Promise<void> {
    if (this.tree && !column.filter?.optionQuery) {
      const records = this.records ?? this.treeCache?.rows ?? [];
      const scoped = projectRecordTree(records, this.tree, this.columns, {...this.internalState, search:'', filters:{}, page:1}, []).scoped;
      const values = new Map<string, RecordListResolvedOption>();
      for (const row of scoped) {
        const value = resolveRecordPath(row, column.key).value;
        if (value !== null && value !== undefined && ['string','number','boolean'].includes(typeof value) && String(value).toLocaleLowerCase().includes(search.toLocaleLowerCase())) values.set(String(value), {value:value as string|number|boolean, label:String(value)});
      }
      const options = [...values.values()].sort((a,b) => a.label.localeCompare(b.label, undefined, {numeric:true}));
      this.optionsByColumn[column.key] = options.slice(0,10);
      this.optionTotals[column.key] = options.length;
      this.cdr.markForCheck();
      return;
    }
    if ((!this.query && !column.filter?.optionQuery) || !column.filter) {
      return;
    }
    const sequence = (this.optionSequence[column.key] ?? 0) + 1;
    this.optionSequence[column.key] = sequence;
    this.optionLoading[column.key] = true;
    this.cdr.markForCheck();
    try {
      const result = await firstValueFrom(this.reportQuery.execute(this.optionQuery(column, search)));
      if (sequence !== this.optionSequence[column.key]) {
        return;
      }
      const specification = column.filter.optionQuery;
      const valueKey = specification?.valueKey ?? 'value';
      const labelKey = specification?.labelKey ?? 'value';
      this.optionsByColumn[column.key] = result.rows
        .filter((row) => resolveRecordPath(row, valueKey).value != null)
        .map((row) => ({
          value: resolveRecordPath(row, valueKey).value as string | number | boolean,
          label:
            resolveRecordPath(row, labelKey).value == null
              ? String(resolveRecordPath(row, valueKey).value)
              : String(resolveRecordPath(row, labelKey).value),
        }));
      this.optionTotals[column.key] = result.meta.total_count ?? result.meta.row_count;
    } catch (error) {
      console.error("[RecordList] Could not load filter options for '" + column.key + "'.", error);
      this.optionsByColumn[column.key] = [];
      this.optionTotals[column.key] = 0;
    } finally {
      if (sequence === this.optionSequence[column.key]) {
        this.optionLoading[column.key] = false;
        this.cdr.detectChanges();
      }
    }
  }

  private optionQuery(column: RecordListColumn, search: string): ReportQuery {
    const specification = column.filter?.optionQuery;
    const sourceQuery = specification?.query ?? this.query;
    if (!sourceQuery) {
      throw new Error('Record-list option query has no RQL source.');
    }
    const query = JSON.parse(JSON.stringify(sourceQuery)) as ReportQuery;
    if (!specification) {
      const field = column.filter?.field ?? column.field;
      query.select = [{ type: 'field', field, as: 'value' }];
      query.group_by = [{ field }];
      query.order_by = [{ ref: 'value', direction: 'asc' }];
      query.having = undefined;
      const nonNull = { type: 'condition', field, operator: 'is_not_null' };
      query.where = query.where
        ? { type: 'group', operator: 'and', children: [query.where, nonNull] }
        : nonNull;
    }
    query.limit = 10;
    query.offset = 0;
    query.include_total = true;
    const searchField =
      specification?.searchField ??
      (!specification ? (column.filter?.field ?? column.field) : undefined);
    if (search && searchField) {
      const searchCondition = {
        type: 'condition',
        field: searchField,
        operator: 'contains',
        value: search,
      };
      query.where = query.where
        ? { type: 'group', operator: 'and', children: [query.where, searchCondition] }
        : searchCondition;
    }
    return query;
  }

  private matchesCondition(condition: RecordListActionCondition): boolean {
    const records = this.selectedRecords;
    if (!records.length) {
      return false;
    }
    const compare = (record: Record<string, unknown>, equal: boolean): boolean => {
      const resolved = resolveRecordPath(record, condition.field);
      if (!resolved.found) {
        this.logOnce(
          'action:' + condition.field,
          "[RecordList] Action condition field '" + condition.field + "' is missing.",
          record,
        );
        return false;
      }
      return equal
        ? resolved.value === condition.value
        : resolved.value !== condition.value;
    };
    switch (condition.operator) {
      case 'all_eq':
        return records.every((record) => compare(record, true));
      case 'all_ne':
        return records.every((record) => compare(record, false));
      case 'some_eq':
        return records.some((record) => compare(record, true));
      case 'some_ne':
        return records.some((record) => compare(record, false));
    }
  }

  private rowId(row: Record<string, unknown>, index: number): string {
    const value = resolveRecordPath(row, this.selectionKey).value;
    if (value == null) {
      this.logOnce(
        'selection:' + this.selectionKey,
        "[RecordList] Selection key '" + this.selectionKey + "' is missing from the RQL output.",
        row,
      );
      return 'row-' + index;
    }
    return String(value);
  }

  private apiErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const candidate = error as {
        error?: { message?: string; error?: { message?: string } };
        message?: string;
      };
      return (
        candidate.error?.error?.message ??
        candidate.error?.message ??
        candidate.message ??
        'Could not load records from RQL.'
      );
    }
    return 'Could not load records from RQL.';
  }

  private logOnce(key: string, message: string, value: unknown): void {
    if (this.loggedErrors.has(key)) {
      return;
    }
    this.loggedErrors.add(key);
    console.error(message, value);
  }

  private configurationError(): string {
    if (this.tree) {
      if (!this.tree.idKey || !this.tree.parentKey) return 'Tree lists require ID and parent fields.';
      if (this.recordsTotal !== null) return 'Tree lists require a complete collection, not a server-paginated fragment.';
      if (this.availableViewTypes.some(mode => mode !== 'list') || this.businessDocument) return 'Tree lists support list view only.';
      if (this.query && (![this.tree.idKey, this.tree.parentKey].every(key => this.query!.select.some(field => field['as'] === key)) || this.query.group_by?.length)) return 'Tree queries must select the ID and parent aliases without grouping.';
    }
    const columnError = validateRecordListColumns(this.columns);
    if (columnError) {
      return columnError;
    }
    const nonListViews = this.availableViewTypes.filter((mode) => mode !== 'list');
    if (nonListViews.length > 0 && !this.cardConfiguration?.component) {
      return `Record-list ${nonListViews.join('/')} views require an owner-provided card component.`;
    }
    if (this.availableViewTypes.includes('kanban') && !this.businessDocument) {
      return 'Kanban view is available only for a configured business document.';
    }
    if (!this.businessDocument) {
      return '';
    }
    if (!this.businessDocument.documentTypeKey.trim()) {
      return 'Business-document record lists require a documentTypeKey.';
    }
    if (this.records) {
      const required = [
        this.businessDocumentField('documentIdField'),
        this.businessDocumentField('documentTypeField'),
        this.businessDocumentField('pipelineIdField'),
        this.businessDocumentField('currentStepIdField'),
        this.businessDocumentField('documentNumberField'),
        this.businessDocumentField('displayNameField'),
      ];
      const invalidIndex = this.records.findIndex((record) => required.some((field) => {
        return !resolveRecordPath(record, field).found;
      }));
      return invalidIndex >= 0
        ? `Business-document record ${invalidIndex + 1} does not satisfy the required row shape.`
        : '';
    }
    if (!this.query?.joins?.some((join) => join.join === 'business_document')) {
      return "Business-document record lists must include the registered 'business_document' RQL join.";
    }
    const aliases = new Set(
      this.query.select
        .map((selection) => selection['as'])
        .filter((value): value is string => typeof value === 'string'),
    );
    const required = [
      this.businessDocumentField('documentIdField'),
      this.businessDocumentField('documentTypeField'),
      this.businessDocumentField('pipelineIdField'),
      this.businessDocumentField('currentStepIdField'),
      this.businessDocumentField('documentNumberField'),
      this.businessDocumentField('displayNameField'),
    ];
    const missing = required.filter((field) => !aliases.has(field));
    return missing.length
      ? `Business-document RQL output is missing: ${missing.join(', ')}.`
      : '';
  }

  private configureViewMode(): void {
    if (this.viewRegistration) {
      this.listView.release(this.viewRegistration);
      this.viewRegistration = undefined;
    }
    this.pipelines = [];
    this.selectedPipelineId = '';
    this.pipelineError = '';
    this.viewMode = 'list';
    const modes = this.availableViewTypes;
    if (modes.length === 0) {
      return;
    }
    const requestedInitial = this.businessDocument?.initialView ?? this.initialView;
    this.viewRegistration = this.listView.register(requestedInitial, modes);
    this.viewMode = this.listView.state.mode;
    if (this.businessDocument) {
      void this.loadPipelines();
    }
  }

  private async loadPipelines(): Promise<void> {
    if (!this.businessDocument) {
      return;
    }
    const documentTypeKey = this.businessDocument.documentTypeKey;
    this.pipelineLoading = true;
    this.pipelineError = '';
    this.cdr.markForCheck();
    try {
      this.pipelines = await firstValueFrom(this.businessDocuments.pipelines(documentTypeKey));
      this.selectedPipelineId =
        this.pipelines.find((bundle) => bundle.pipeline.id === this.businessDocument?.pipelineId)?.pipeline.id ??
        this.pipelines.find((bundle) => bundle.pipeline.is_default)?.pipeline.id ??
        this.pipelines[0]?.pipeline.id ??
        '';
      if (!this.selectedPipelineId) {
        this.pipelineError = `No active pipeline is configured for ${documentTypeKey}.`;
      }
      if (this.viewMode === 'kanban') {
        await this.loadRecords();
      }
    } catch (error) {
      this.pipelines = [];
      this.selectedPipelineId = '';
      this.pipelineError = this.apiErrorMessage(error);
      console.error('[RecordList] Could not load business-document pipelines.', error);
    } finally {
      this.pipelineLoading = false;
      this.cdr.detectChanges();
    }
  }

  private businessDocumentQuery(query: ReportQuery): ReportQuery {
    if (!this.businessDocument) {
      return query;
    }
    const scoped = JSON.parse(JSON.stringify(query)) as ReportQuery;
    const conditions: Array<Record<string, unknown>> = [
      {
        type: 'condition',
        field: 'business_document.document_type_key',
        operator: 'eq',
        value: this.businessDocument.documentTypeKey,
      },
    ];
    if (this.viewMode === 'kanban' && this.selectedPipelineId) {
      conditions.push({
        type: 'condition',
        field: 'business_document.pipeline_id',
        operator: 'eq',
        value: this.selectedPipelineId,
      });
    }
    if (scoped.where) {
      conditions.unshift(scoped.where);
    }
    scoped.where =
      conditions.length === 1
        ? conditions[0]
        : { type: 'group', operator: 'and', children: conditions };
    return scoped;
  }

  private get selectedPipeline(): BusinessDocumentPipelineBundle | undefined {
    return this.pipelines.find((bundle) => bundle.pipeline.id === this.selectedPipelineId);
  }

  private businessDocumentField(
    key:
      | 'documentIdField'
      | 'documentTypeField'
      | 'pipelineIdField'
      | 'currentStepIdField'
      | 'documentNumberField'
      | 'displayNameField',
  ): string {
    const defaults = {
      documentIdField: 'business_document.id',
      documentTypeField: 'business_document.document_type_key',
      pipelineIdField: 'business_document.pipeline_id',
      currentStepIdField: 'business_document.current_step_id',
      documentNumberField: 'business_document.document_number',
      displayNameField: 'business_document.display_name',
    };
    return this.businessDocument?.[key] ?? defaults[key];
  }

  private businessDocumentValue(
    row: Record<string, unknown>,
    key:
      | 'documentIdField'
      | 'documentTypeField'
      | 'pipelineIdField'
      | 'currentStepIdField'
      | 'documentNumberField'
      | 'displayNameField',
  ): unknown {
    return resolveRecordPath(row, this.businessDocumentField(key)).value;
  }

  private kanbanCard(row: Record<string, unknown>) {
    const id = String(this.businessDocumentValue(row, 'documentIdField') ?? '');
    return {
      id,
      record: row,
      disabled: !id,
    };
  }
}
